/**
 * Real-Time Build Progress with SSE Streaming
 * Shows live command execution and per-provider status with collapsible cards
 */

import React, { useEffect, useState, useRef } from 'react';
import { getApiUrl } from '../../utils/api';
import {
  getInstanceExplanation,
  getCommandPurpose,
  translateError,
  getErrorActionability
} from '../../utils/buildExplainers';
import {
  BuildPhaseTimeline,
  createDefaultPhases,
  updatePhaseStatus,
  calculateProgress,
  type PhaseInfo
} from './BuildPhaseTimeline';

interface CommandStatus {
  index: number;
  command: string;
  status: 'pending' | 'running' | 'success' | 'failed' | 'ai_fixed';
  startTime?: number;
  duration?: number;
  stdout?: string;
  stderr?: string;
  aiAnalysis?: string;
  aiFix?: {
    original: string;
    fixed: string;
    reason: string;
  };
}

interface ProviderState {
  provider: string;
  status: 'queued' | 'launching' | 'testing' | 'building' | 'complete' | 'failed';
  instanceType?: string;
  region?: string;
  commands: CommandStatus[];
  currentCommandIndex?: number;
  aiFixesApplied: number;
  duration?: number;
  rcaActivity?: RCAActivity;
  statusDescription?: string;
}

interface RCAActivity {
  active: boolean;
  currentIteration: number;
  maxIterations: number;
  command: string;
  lastDiagnosis: string;
  diagnosticsRunning: boolean;
  attempts: Array<{
    iteration: number;
    diagnosis: string;
    suggestedFix: string;
    success: boolean;
  }>;
}

interface BuildProgressStreamProps {
  templateId: number;
  templateName: string;
  templateType: string;
  providers: string[];
  jobIds?: number[];
  onComplete?: (success: boolean) => void;
}

export const BuildProgressStream: React.FC<BuildProgressStreamProps> = ({
  templateId,
  templateName,
  templateType,
  providers,
  jobIds,
  onComplete
}) => {
  const [providerStates, setProviderStates] = useState<Map<string, ProviderState>>(new Map());
  const [infraHealthChecks] = useState<Array<{check: string, status: 'pass' | 'warn' | 'fail', message: string}>>([]);
  const [buildPhase, setBuildPhase] = useState<'testing' | 'building' | 'complete' | 'failed'>('testing');
  const [expandedInstances, setExpandedInstances] = useState<Set<string>>(new Set());
  const [rcaActivity, setRcaActivity] = useState<Map<string, RCAActivity>>(new Map());
  const [imageSelections, setImageSelections] = useState<Map<string, any>>(new Map());
  // Inline action cards for VM test failures
  const [testActions, setTestActions] = useState<Array<{
    provider: string;
    jobId: number | null;
    error: string;
    aiSuggestions?: any;
    detailedResults?: any[];
    actions: string[];
  }>>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [collapsedProviders, setCollapsedProviders] = useState<Set<string>>(new Set());
  const autoCollapsedRef = useRef<Set<string>>(new Set());
  const eventSourceRef = useRef<EventSource | null>(null);

  // Phase timeline state
  const [phases, setPhases] = useState<PhaseInfo[]>(createDefaultPhases());
  const [currentPhaseId, setCurrentPhaseId] = useState<string>('validation');
  const [statusDescription, setStatusDescription] = useState<string>('');

  // Stable provider key to prevent resets on array reference changes
  const providersKey = providers.slice().sort().join(',');
  const initializedRef = useRef<string | null>(null);

  // Initialize provider states (reset only on actual template/provider changes)
  useEffect(() => {
    const currentKey = `${templateId}:${providersKey}`;

    // Skip if already initialized with the same key
    if (initializedRef.current === currentKey) {
      return;
    }

    console.log('🔄 Resetting provider states and RCA for templateId:', templateId, 'providers:', providersKey);
    initializedRef.current = currentKey;

    const initialStates = new Map<string, ProviderState>();
    providers.forEach(provider => {
      initialStates.set(provider, {
        provider,
        status: 'queued',
        commands: [],
        aiFixesApplied: 0
      });
    });
    setProviderStates(initialStates);
    setRcaActivity(new Map());  // Clear RCA activity on template change
  }, [providers, providersKey, templateId]);

  // Connect to SSE stream
  useEffect(() => {
    console.log('🔍 BuildProgressStream useEffect running:', {templateId, providers});

    // Check both possible token keys (matches AdminAMIBuilder pattern)
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');

    console.log('🔍 Token check:', {
      hasAuthToken: !!localStorage.getItem('authToken'),
      hasToken: !!localStorage.getItem('token'),
      usingToken: !!token
    });

    if (!token) {
      console.error('❌ No authentication token found in localStorage (checked authToken and token)');
      return;
    }

    const sseUrl = `${getApiUrl()}/api/v1/multi-cloud/images/templates/${templateId}/build-stream?token=${encodeURIComponent(token)}`;
    console.log('🔍 Creating EventSource:', sseUrl);

    // EventSource doesn't support custom headers, so we pass token as query parameter
    const eventSource = new EventSource(sseUrl);

    eventSourceRef.current = eventSource;

    // FIX: Add onopen handler to confirm connection
    eventSource.onopen = () => {
      console.log('✅ SSE connection opened successfully');
    };

    // FIX: Add generic message handler to catch all events (for debugging)
    eventSource.onmessage = (messageEvent) => {
      console.log('📡 RAW SSE MESSAGE:', {
        type: messageEvent.type,
        data: messageEvent.data,
        lastEventId: messageEvent.lastEventId
      });
    };

    console.log('✅ EventSource created, adding event listeners...');

    // Event: Build started - Only reset if this is a NEW build (not reconnection)
    eventSource.addEventListener('build_started', (e) => {
      const data = JSON.parse(e.data);
      console.log('📡 SSE Event - build_started:', data);

      // Only reset states if they don't already have meaningful data
      // This prevents losing state on SSE reconnection
      setProviderStates(prev => {
        const hasExistingData = Array.from(prev.values()).some(
          s => s.commands.length > 0 || s.status !== 'queued'
        );
        if (hasExistingData) {
          console.log('   Skipping reset - states already have data (SSE reconnection)');
          return prev;
        }
        console.log('🔄 Resetting provider states for new build');
        const resetStates = new Map<string, ProviderState>();
        providers.forEach(provider => {
          resetStates.set(provider, {
            provider,
            status: 'queued',
            commands: [],
            aiFixesApplied: 0
          });
        });
        return resetStates;
      });

      // Reset RCA activity and collapse state
      setRcaActivity(new Map());
      setCollapsedProviders(new Set());
      autoCollapsedRef.current = new Set();

      // Update phase: Start validation phase
      setPhases(prevPhases => updatePhaseStatus(prevPhases, 'validation', {
        status: 'in_progress',
        startTime: new Date().toISOString(),
        details: [
          'Verifying template configuration',
          `Preparing ${data.providers.length} provider build${data.providers.length > 1 ? 's' : ''}`,
          data.run_vm_testing ? 'VM testing enabled' : 'VM testing skipped'
        ]
      }));
      setCurrentPhaseId('validation');
      setStatusDescription(
        `Starting build pipeline for ${data.template_name || templateName} (${data.template_type || templateType}) across ${data.providers.length} provider${data.providers.length > 1 ? 's' : ''}. Pre-flight VM testing ${data.run_vm_testing ? 'enabled' : 'skipped'}.`
      );
    });

    // Event: VM testing started - Clear old commands
    eventSource.addEventListener('vm_testing_start', (e) => {
      const data = JSON.parse(e.data);
      console.log('📡 SSE Event - vm_testing_start:', data);

      // Only process events for providers this component is tracking
      if (!providers.includes(data.provider)) {
        console.log(`   Skipping - not tracking provider ${data.provider}`);
        return;
      }

      // Update phases: Complete validation, start testing
      setPhases(prevPhases => {
        // First mark validation as completed
        const withValidation = updatePhaseStatus(prevPhases, 'validation', {
          status: 'completed',
          endTime: new Date().toISOString(),
          duration: prevPhases.find(p => p.id === 'validation')?.startTime
            ? (Date.now() - new Date(prevPhases.find(p => p.id === 'validation')!.startTime!).getTime()) / 1000
            : 1
        });

        // Then start testing phase
        return updatePhaseStatus(withValidation, 'testing', {
          status: 'in_progress',
          startTime: new Date().toISOString(),
          details: [`Testing ${data.provider} on ${data.instance_type}`]
        });
      });
      setCurrentPhaseId('testing');

      setProviderStates(prev => {
        console.log('🔍 Updating state for provider:', data.provider);
        console.log('   Previous state:', prev.get(data.provider));
        const newStates = new Map(prev);

        // Create fresh state with no old commands
        const updated: ProviderState = {
          provider: data.provider,
          status: 'testing' as const,
          instanceType: data.instance_type,
          region: data.region,
          commands: [],  // Clear old commands
          aiFixesApplied: 0  // Reset counter
        };

        console.log('   New state:', updated);
        newStates.set(data.provider, updated);
        return newStates;
      });
      setStatusDescription(
        `Testing ${data.provider.toUpperCase()} on ${data.instance_type}${data.region ? ` in ${data.region}` : ''}. Running commands in a real VM to catch failures before the full image build.`
      );
    });

    // Event: VM testing complete
    eventSource.addEventListener('vm_testing_complete', (e) => {
      const data = JSON.parse(e.data);
      console.log('📡 SSE Event - vm_testing_complete:', data);

      // Only process events for providers this component is tracking
      if (!providers.includes(data.provider)) {
        console.log(`   Skipping - not tracking provider ${data.provider}`);
        return;
      }

      // Update testing phase
      const testDuration = data.test_duration_sec || data.duration || 0;
      setPhases(prevPhases => updatePhaseStatus(prevPhases, 'testing', {
        status: data.success ? 'completed' : 'failed',
        endTime: new Date().toISOString(),
        duration: testDuration,
        details: data.success ? [
          'All providers tested successfully',
          `${data.fixes_applied || 0} AI fixes applied`
        ] : undefined,
        errors: data.success ? undefined : [data.error_summary || 'Testing failed']
      }));

      setProviderStates(prev => {
        const newStates = new Map(prev);
        const state = newStates.get(data.provider);
        if (state) {
          // Create completely new object for React to detect change
          const commands = data.detailed_results ? data.detailed_results.map((result: any, idx: number) => ({
            index: idx,
            command: result.command,
            status: result.success ? (result.ai_fixed ? 'ai_fixed' : 'success') : 'failed',
            duration: result.duration,
            stdout: result.stdout,
            stderr: result.error,
            aiAnalysis: result.ai_diagnosis,
            aiFix: result.ai_fixed ? {
              original: result.original_command,
              fixed: result.command,
              reason: result.ai_diagnosis || 'AI auto-fix applied'
            } : undefined
          })) : state.commands;

          newStates.set(data.provider, {
            ...state,
            status: data.success ? 'complete' : 'failed',
            duration: data.duration,
            aiFixesApplied: data.fixes_applied || 0,
            commands
          });
        }
        return newStates;
      });

      if (data.success) {
        const cmdCount = data.detailed_results?.length || 0;
        const fixCount = data.fixes_applied || 0;
        const duration = data.duration ? `${data.duration.toFixed(0)}s` : '';
        setStatusDescription(
          `${data.provider.toUpperCase()} validation passed — ${cmdCount} commands in ${duration}.${fixCount > 0 ? ` AI auto-fixed ${fixCount} issue${fixCount > 1 ? 's' : ''}.` : ''}`
        );
      } else {
        const failIdx = data.detailed_results?.findIndex((r: any) => !r.success) ?? -1;
        const total = data.detailed_results?.length || 0;
        setStatusDescription(
          `${data.provider.toUpperCase()} validation failed${failIdx >= 0 ? ` at command ${failIdx + 1}/${total}` : ''}. Review errors or accept AI fixes.`
        );
      }
    });

    // Event: Phase change
    eventSource.addEventListener('build_phase_change', (e) => {
      const data = JSON.parse(e.data);
      console.log('📡 SSE Event - build_phase_change:', data);
      if (data.phase === 'packer_build') {
        setBuildPhase('building');

        // Update phase to building
        setPhases(prevPhases => updatePhaseStatus(prevPhases, 'building', {
          status: 'in_progress',
          startTime: new Date().toISOString()
        }));
        setCurrentPhaseId('building');
        setStatusDescription('All providers validated. Starting Packer image build — creating production machine images (typically 20-40 min).');
      }
    });

    // Event: Packer build triggered
    eventSource.addEventListener('packer_build_triggered', (e) => {
      const data = JSON.parse(e.data);
      console.log('📡 SSE Event - packer_build_triggered:', data);
    });

    // Event: Provider-specific build started (transitions provider from queued -> building)
    eventSource.addEventListener('provider_build_started', (e) => {
      const data = JSON.parse(e.data);
      console.log('📡 SSE Event - provider_build_started:', data);

      if (!data.provider || !providers.includes(data.provider)) return;

      setProviderStates(prev => {
        const newStates = new Map(prev);
        const state = newStates.get(data.provider);
        if (state) {
          newStates.set(data.provider, {
            ...state,
            status: 'building',
            region: data.region || state.region,
            statusDescription: 'Packer build in progress...'
          });
        }
        return newStates;
      });
    });

    // Heartbeat
    eventSource.addEventListener('heartbeat', (e) => {
      console.log('💓 SSE heartbeat');
    });

    // Image Selection Events - Show intelligent base image selection
    eventSource.addEventListener('image_selection_start', (e) => {
      const data = JSON.parse(e.data);
      console.log('🎯 Image Selection Start:', data);
      if (!providers.includes(data.provider)) return;
      setImageSelections(prev => {
        const newMap = new Map(prev);
        newMap.set(data.provider, {
          status: 'analyzing',
          requirements: data.requirements
        });
        return newMap;
      });
    });

    eventSource.addEventListener('image_candidates_found', (e) => {
      const data = JSON.parse(e.data);
      console.log('📋 Image Candidates Found:', data);
      if (!providers.includes(data.provider)) return;
      setImageSelections(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(data.provider) || {};
        newMap.set(data.provider, {
          ...current,
          status: 'selecting',
          candidates: data.top_3,
          candidateCount: data.count
        });
        return newMap;
      });
    });

    eventSource.addEventListener('image_selected', (e) => {
      const data = JSON.parse(e.data);
      console.log('✅ Image Selected:', data);
      if (!providers.includes(data.provider)) return;
      setImageSelections(prev => {
        const newMap = new Map(prev);
        newMap.set(data.provider, {
          status: 'selected',
          selectedImage: data.image_name,
          score: data.score,
          reason: data.reason,
          has_conda: data.has_conda,
          has_cuda: data.has_cuda
        });
        return newMap;
      });
    });

    // RCA Events - Real-time agent debugging transparency
    eventSource.addEventListener('rca_iteration_start', (e) => {
      const data = JSON.parse(e.data);
      console.log('🔍 RCA Iteration Start:', data);
      if (!providers.includes(data.provider)) return;
      setRcaActivity(prev => {
        const newMap = new Map(prev);
        newMap.set(data.provider, {
          active: true,
          currentIteration: data.iteration,
          maxIterations: data.max_iterations,
          command: data.command,
          lastDiagnosis: data.error_preview || '',
          diagnosticsRunning: false,
          attempts: prev.get(data.provider)?.attempts || []
        });
        return newMap;
      });
    });

    eventSource.addEventListener('rca_diagnostic_request', (e) => {
      const data = JSON.parse(e.data);
      console.log('🔬 RCA Diagnostics Requested:', data);
      if (!providers.includes(data.provider)) return;
      setRcaActivity(prev => {
        const newMap = new Map(prev);
        const activity = newMap.get(data.provider);
        if (activity) {
          newMap.set(data.provider, {
            ...activity,
            diagnosticsRunning: true,
            lastDiagnosis: data.reason || 'Exploring filesystem...'
          });
        }
        return newMap;
      });
    });

    eventSource.addEventListener('rca_diagnostic_complete', (e) => {
      const data = JSON.parse(e.data);
      console.log('✅ RCA Diagnostic Complete:', data);
    });

    eventSource.addEventListener('rca_fix_attempt', (e) => {
      const data = JSON.parse(e.data);
      console.log('🤖 RCA Fix Attempt:', data);
      if (!providers.includes(data.provider)) return;
      setRcaActivity(prev => {
        const newMap = new Map(prev);
        const activity = newMap.get(data.provider);
        if (activity) {
          activity.attempts.push({
            iteration: data.iteration,
            diagnosis: data.diagnosis || data.explanation || '',
            suggestedFix: data.suggested_fix,
            success: null as any  // Will be updated on success/fail
          });
          newMap.set(data.provider, {...activity, diagnosticsRunning: false});
        }
        return newMap;
      });
    });

    eventSource.addEventListener('rca_fix_success', (e) => {
      const data = JSON.parse(e.data);
      console.log('✅ RCA Fix Success:', data);
      if (!providers.includes(data.provider)) return;
      setRcaActivity(prev => {
        const newMap = new Map(prev);
        const activity = newMap.get(data.provider);
        if (activity && activity.attempts.length > 0) {
          activity.attempts[activity.attempts.length - 1].success = true;
          newMap.set(data.provider, {...activity, active: false, diagnosticsRunning: false});
        }
        return newMap;
      });
    });

    eventSource.addEventListener('rca_fix_failed', (e) => {
      const data = JSON.parse(e.data);
      console.log('❌ RCA Fix Failed:', data);
      if (!providers.includes(data.provider)) return;
      setRcaActivity(prev => {
        const newMap = new Map(prev);
        const activity = newMap.get(data.provider);
        if (activity && activity.attempts.length > 0) {
          activity.attempts[activity.attempts.length - 1].success = false;
          // Keep active if will retry
          if (!data.will_retry) {
            activity.active = false;
          }
          newMap.set(data.provider, {...activity});
        }
        return newMap;
      });
    });

    // Event: VM test requires user action (inline decision card)
    eventSource.addEventListener('vm_test_action_required', (e) => {
      const data = JSON.parse(e.data);
      console.log('📡 SSE Event - vm_test_action_required:', data);
      if (!providers.includes(data.provider)) return;
      setTestActions(prev => [
        ...prev.filter(a => a.provider !== data.provider),
        {
          provider: data.provider,
          jobId: data.job_id,
          error: data.error,
          aiSuggestions: data.ai_suggestions,
          detailedResults: data.detailed_results,
          actions: data.actions || ['build_anyway', 'cancel']
        }
      ]);
    });

    // Event: Build error (handles both provider-specific and global errors)
    eventSource.addEventListener('build_error', (e) => {
      const data = JSON.parse(e.data);
      console.log('📡 SSE Event - build_error:', data);

      // Check if this is a provider-specific error
      if (data.provider && providers.includes(data.provider)) {
        // Provider-specific error - only mark that provider as failed
        setProviderStates(prev => {
          const newStates = new Map(prev);
          const state = newStates.get(data.provider);
          if (state) {
            newStates.set(data.provider, { ...state, status: 'failed' });
          }
          return newStates;
        });

        // Update phase status with provider-specific error
        setPhases(prevPhases => updatePhaseStatus(prevPhases, data.phase || 'building', {
          status: 'in_progress',  // Keep in progress if other providers might still succeed
          errors: [...(prevPhases.find(p => p.id === (data.phase || 'building'))?.errors || []), `${data.provider.toUpperCase()}: ${data.message}`]
        }));

        setStatusDescription(`${data.provider.toUpperCase()} build failed: ${data.message}`);

        // Check if ALL providers have now failed
        const allProvidersFailed = providers.every(p => {
          const state = providerStates.get(p);
          return state?.status === 'failed' || (p === data.provider);
        });

        if (allProvidersFailed) {
          setBuildPhase('failed');
          onComplete?.(false);
        }
      } else {
        // Global error - affects all providers
        setBuildPhase('failed');
        setPhases(prevPhases => updatePhaseStatus(prevPhases, data.phase || 'building', {
          status: 'failed',
          errors: [data.message]
        }));
        setStatusDescription(`Build failed: ${data.message}`);
        onComplete?.(false);
      }
    });

    // Event: Build success (provider-specific)
    eventSource.addEventListener('build_success', (e) => {
      const data = JSON.parse(e.data);
      console.log('📡 SSE Event - build_success:', data);

      if (data.provider && providers.includes(data.provider)) {
        // Mark this provider as complete
        setProviderStates(prev => {
          const newStates = new Map(prev);
          const state = newStates.get(data.provider);
          if (state) {
            newStates.set(data.provider, { ...state, status: 'complete' });
          }
          return newStates;
        });

        setStatusDescription(`${data.provider.toUpperCase()} image built successfully`);

        // Check if ALL providers have now completed (success or failure)
        setTimeout(() => {
          setProviderStates(currentStates => {
            const allComplete = providers.every(p => {
              const state = currentStates.get(p);
              return state?.status === 'complete' || state?.status === 'failed';
            });

            if (allComplete) {
              const anySuccess = providers.some(p => currentStates.get(p)?.status === 'complete');
              setBuildPhase(anySuccess ? 'complete' : 'failed');
              setPhases(prevPhases => updatePhaseStatus(prevPhases, 'building', {
                status: anySuccess ? 'completed' : 'failed'
              }));
              onComplete?.(anySuccess);
            }

            return currentStates;
          });
        }, 100);  // Small delay to ensure state is updated
      }
    });

    // Event: AI narration during packer build (per-provider isolation)
    eventSource.addEventListener('build_narration', (e) => {
      const data = JSON.parse(e.data);
      console.log('📡 SSE Event - build_narration:', data);

      // Filter by specific provider for proper isolation
      if (data.provider) {
        // New format: single provider field
        if (!providers.includes(data.provider)) {
          console.log(`   Skipping - narration for ${data.provider} not in tracked providers [${providers.join(', ')}]`);
          return;
        }
      } else if (data.providers && Array.isArray(data.providers)) {
        // Legacy format: providers array (backwards compatibility)
        const hasTrackedProvider = data.providers.some((p: string) => providers.includes(p));
        if (!hasTrackedProvider) {
          console.log('   Skipping - narration not for tracked providers');
          return;
        }
      }

      if (data.narration) {
        setStatusDescription(data.narration);
      }
    });

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-collapse completed provider cards after 3s (failed stays expanded)
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    providerStates.forEach((state, provider) => {
      if (state.status === 'complete' && !autoCollapsedRef.current.has(provider)) {
        autoCollapsedRef.current.add(provider);
        timers.push(setTimeout(() => {
          setCollapsedProviders(prev => { const next = new Set(Array.from(prev)); next.add(provider); return next; });
        }, 3000));
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [providerStates]);

  const handleBuildAction = async (action: string) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const jobIds = testActions.map(a => a.jobId).filter(Boolean) as number[];

      const response = await fetch(
        `${getApiUrl()}/api/v1/multi-cloud/images/templates/${templateId}/build-action`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ action, job_ids: jobIds })
        }
      );

      if (response.ok) {
        setTestActions([]);  // Clear action cards
        if (action === 'cancel') {
          setBuildPhase('failed');
          onComplete?.(false);
        }
      } else {
        console.error('Build action failed:', await response.text());
      }
    } catch (err) {
      console.error('Build action error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✅';
      case 'failed': return '❌';
      case 'running': return '🔄';
      case 'ai_fixed': return '🔧';
      case 'pending': return '⏸';
      default: return '⏸';
    }
  };

  const getProviderStatusColor = (status: string) => {
    switch (status) {
      case 'complete': return 'bg-green-900/30 border-green-500/50';
      case 'failed': return 'bg-red-900/30 border-red-500/50';
      case 'testing': return 'bg-blue-900/30 border-blue-500/50';
      case 'launching': return 'bg-yellow-900/30 border-yellow-500/50';
      default: return 'bg-gray-900/30 border-gray-700';
    }
  };

  return (
    <div className="space-y-4">
      {/* Progress Bar + Compact Phase Timeline */}
      <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-white">Build Pipeline</span>
            <span className="text-xs text-gray-500">&mdash;</span>
            <span className="text-xs text-gray-400">{templateName}</span>
            <span className="text-xs text-gray-600">({templateType})</span>
            {providers.map(p => (
              <span key={p} className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-300">
                {p}
              </span>
            ))}
            {jobIds && jobIds.length > 0 && (
              <span className="text-xs text-gray-500">
                Job {jobIds.map(id => `#${id}`).join(', ')}
              </span>
            )}
          </div>
          <span className="text-sm text-gray-400">{calculateProgress(phases)}%</span>
        </div>
        <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden mb-3">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
            style={{ width: `${calculateProgress(phases)}%` }}
          />
        </div>
        <BuildPhaseTimeline phases={phases} currentPhaseId={currentPhaseId} compact={true} inline={true} />

        {/* Validation Issues — collapsible inside pipeline card */}
        {testActions.length > 0 && (
          <details open className="border-t border-gray-700/50 mt-3 pt-3">
            <summary className="text-xs font-semibold text-yellow-300 cursor-pointer hover:text-yellow-200 flex items-center gap-1.5">
              <span>&#9888;</span>
              {testActions.length} Validation Issue{testActions.length > 1 ? 's' : ''} Require{testActions.length === 1 ? 's' : ''} Attention
            </summary>
            <div className="mt-3 space-y-3">
              {testActions.map(action => (
                <div key={action.provider} className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-2xl">&#9888;</span>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-yellow-300">
                        VM Test Failed ({action.provider.toUpperCase()})
                        {action.jobId && <span className="text-xs text-gray-500 ml-2 font-normal">Job #{action.jobId}</span>}
                      </h4>
                      <p className="text-xs text-gray-400 mt-1">{action.error}</p>
                    </div>
                  </div>

                  {action.aiSuggestions && (
                    <div className="bg-blue-900/20 border border-blue-500/20 rounded p-3 mb-3">
                      <p className="text-xs text-blue-300 font-medium mb-2">AI has suggested fixes:</p>
                      <div className="space-y-1">
                        {(Array.isArray(action.aiSuggestions) ? action.aiSuggestions : [action.aiSuggestions])
                          .slice(0, 3)
                          .map((suggestion: any, idx: number) => (
                            <div key={idx} className="text-xs text-gray-400">
                              <span className="text-gray-500">#{idx + 1}</span>{' '}
                              {suggestion.original_command?.slice(0, 60)} -&gt;{' '}
                              <span className="text-green-400">{suggestion.fixed_command?.slice(0, 60)}</span>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}

                  {action.detailedResults && action.detailedResults.length > 0 && (
                    <div className="mb-3 text-xs text-gray-500">
                      {action.detailedResults.filter((r: any) => r.success).length}/{action.detailedResults.length} commands passed
                    </div>
                  )}

                  <div className="flex gap-2">
                    {action.actions.includes('accept_ai_fixes') && (
                      <button
                        onClick={() => handleBuildAction('accept_ai_fixes')}
                        disabled={actionLoading}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white text-xs rounded-lg font-medium transition-colors"
                      >
                        {actionLoading ? 'Applying...' : 'Accept AI Fixes'}
                      </button>
                    )}
                    {action.actions.includes('build_anyway') && (
                      <button
                        onClick={() => handleBuildAction('build_anyway')}
                        disabled={actionLoading}
                        className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 text-white text-xs rounded-lg font-medium transition-colors"
                      >
                        {actionLoading ? 'Processing...' : 'Build Anyway'}
                      </button>
                    )}
                    {action.actions.includes('cancel') && (
                      <button
                        onClick={() => handleBuildAction('cancel')}
                        disabled={actionLoading}
                        className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-700 text-white text-xs rounded-lg font-medium transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Contextual Status Description — inside pipeline card */}
        {statusDescription && (
          <div className="border-t border-gray-700/50 mt-3 pt-2">
            <p className="text-sm text-gray-300 leading-relaxed">{statusDescription}</p>
          </div>
        )}

        {/* Real-Time RCA Activity — inside pipeline card */}
        {/* Show both active RCA sessions (with animation) and completed sessions */}
        {Array.from(rcaActivity.values()).filter(rca => rca.command).map(rca => (
          <div key={rca.command} className={`bg-gradient-to-r from-purple-900/20 to-indigo-900/20 border-l-4 ${rca.active ? 'border-purple-500 animate-pulse' : 'border-gray-600'} rounded p-3 mt-3`}>
            <div className="flex items-start gap-3">
              <span className="text-2xl">🔍</span>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-purple-300">
                  Agent Debugging: {rca.command?.split(' ')[0] || 'Command'}
                </h4>
                <p className="text-xs text-gray-400 mt-1">
                  Iteration {rca.currentIteration}/{rca.maxIterations} - Investigating failure
                </p>

                <div className="mt-2 bg-gray-900/50 rounded p-2">
                  <code className="text-xs text-blue-300 block">{rca.command}</code>
                </div>

                {rca.diagnosticsRunning && (
                  <div className="mt-2 bg-blue-900/20 border border-blue-500/30 rounded p-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <p className="text-xs text-blue-300">
                        {rca.lastDiagnosis}
                      </p>
                    </div>
                  </div>
                )}

                {rca.attempts.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    <p className="text-xs text-gray-500 font-medium">Agent's investigation:</p>
                    {rca.attempts.map((attempt, idx) => (
                      <div key={idx} className="flex items-start gap-2 bg-gray-900/30 rounded p-2">
                        <span className="text-sm mt-0.5">
                          {attempt.success === true ? '✅' : attempt.success === false ? '❌' : '🔄'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-400 leading-relaxed">{attempt.diagnosis}</p>
                          {attempt.suggestedFix && (
                            <code className="text-xs text-gray-500 block mt-1 truncate">
                              Try: {attempt.suggestedFix}
                            </code>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Per-Provider Detail — collapsible inside pipeline card */}
        <details open className="border-t border-gray-700/50 mt-3 pt-3">
          <summary className="text-xs font-semibold text-gray-300 cursor-pointer hover:text-gray-200 uppercase tracking-wide">
            Provider Detail
          </summary>
          <div className={`mt-3 grid ${providers.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-3 overflow-hidden`}>
        {Array.from(providerStates.values()).map(providerState => (
          <div
            key={providerState.provider}
            className={`border rounded-lg p-3 overflow-hidden ${getProviderStatusColor(providerState.status)}`}
          >
            {/* Provider Header - Clickable */}
            <div
              className="flex items-center justify-between mb-3 cursor-pointer select-none"
              onClick={() => {
                setCollapsedProviders(prev => {
                  const next = new Set(prev);
                  if (next.has(providerState.provider)) {
                    next.delete(providerState.provider);
                  } else {
                    next.add(providerState.provider);
                  }
                  return next;
                });
              }}
            >
              <div className="flex items-center gap-2">
                <span className={`text-xs transition-transform ${collapsedProviders.has(providerState.provider) ? '' : 'rotate-90'}`}>&#9654;</span>
                <h3 className="font-semibold text-white text-sm">
                  {providerState.provider.toUpperCase()}
                </h3>
                {(() => {
                  const label = providerState.status === 'complete' && buildPhase === 'testing'
                    ? 'validated'
                    : providerState.status;
                  return (
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                      providerState.status === 'complete' ? 'bg-green-600/30 text-green-300' :
                      providerState.status === 'failed' ? 'bg-red-600/30 text-red-300' :
                      providerState.status === 'testing' ? 'bg-blue-600/30 text-blue-300 animate-pulse' :
                      providerState.status === 'building' ? 'bg-amber-600/30 text-amber-300 animate-pulse' :
                      'bg-gray-600/30 text-gray-300'
                    }`}>
                      {label}
                    </span>
                  );
                })()}
                {providerState.aiFixesApplied > 0 && (
                  <span className="text-xs bg-blue-600/30 text-blue-300 px-1.5 py-0.5 rounded font-medium">
                    {providerState.aiFixesApplied} fix{providerState.aiFixesApplied > 1 ? 'es' : ''}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {providerState.duration && (
                  <span className="text-xs text-gray-400">{providerState.duration.toFixed(1)}s</span>
                )}
                {collapsedProviders.has(providerState.provider) && providerState.commands.length > 0 && (
                  <span className="text-xs text-gray-500">
                    {providerState.commands.filter(c => c.status === 'success' || c.status === 'ai_fixed').length}/{providerState.commands.length} validated
                  </span>
                )}
              </div>
            </div>

            {!collapsedProviders.has(providerState.provider) && (<>
            {/* Instance Details with Explainer */}
            {providerState.instanceType && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-500">Testing on:</span>
                  <code className="text-xs text-white font-medium">{providerState.instanceType}</code>
                  {providerState.region && (
                    <span className="text-xs text-gray-500">in {providerState.region}</span>
                  )}
                  <button
                    onClick={() => {
                      const newExpanded = new Set(expandedInstances);
                      if (newExpanded.has(providerState.provider)) {
                        newExpanded.delete(providerState.provider);
                      } else {
                        newExpanded.add(providerState.provider);
                      }
                      setExpandedInstances(newExpanded);
                    }}
                    className="text-blue-400 hover:text-blue-300 text-xs"
                    title="Learn about this instance type"
                  >
                    ⓘ
                  </button>
                </div>
                {expandedInstances.has(providerState.provider) && (() => {
                  const info = getInstanceExplanation(providerState.instanceType, providerState.provider);
                  return (
                    <div className="text-xs bg-gray-800/50 border border-gray-700/30 rounded p-2 space-y-1">
                      <p className="text-gray-300">{info.description}</p>
                      <p className="text-gray-400"><span className="text-gray-500">Specs:</span> {info.specs}</p>
                      <p className="text-gray-400"><span className="text-gray-500">Cost:</span> {info.cost}</p>
                      <p className="text-blue-300 bg-blue-900/20 rounded px-2 py-1 mt-1">
                        <span className="font-medium">Why:</span> {info.useCase}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Image Selection - Inline */}
            {imageSelections.get(providerState.provider)?.status === 'selected' && (() => {
              const sel = imageSelections.get(providerState.provider);
              return (
                <div className="mb-3 flex items-center gap-2 text-xs bg-gray-800/40 border border-gray-700/30 rounded p-2">
                  <span className="text-green-400">&#10003;</span>
                  <span className="text-gray-400">Base:</span>
                  <span className="text-gray-300 font-medium">{sel.selectedImage}</span>
                  {sel.has_conda && <span className="text-blue-400 text-[10px]">conda</span>}
                  {sel.has_cuda && <span className="text-purple-400 text-[10px]">cuda</span>}
                  {sel.score && <span className="text-gray-500 ml-auto">score: {sel.score.toFixed(2)}</span>}
                </div>
              );
            })()}

            {/* Command Timeline - Compact */}
            {providerState.commands.length > 0 ? (
              <div className="space-y-1.5">
                {providerState.commands.map((cmd, idx) => (
                  <details
                    key={idx}
                    className="bg-gray-900/30 border border-gray-700/50 rounded overflow-hidden"
                    open={cmd.status === 'running' || cmd.status === 'failed' || cmd.status === 'ai_fixed'}
                  >
                    <summary className="px-2 py-1.5 cursor-pointer hover:bg-gray-800/50 transition-colors">
                      <div className="flex items-start gap-2">
                        <span className="text-sm mt-0.5">{getStatusIcon(cmd.status)}</span>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <code className="text-xs text-gray-300 block truncate max-w-full">{cmd.command}</code>
                          <span className="text-xs text-gray-500 block mt-0.5">
                            {getCommandPurpose(cmd.command)}
                          </span>
                        </div>
                        {cmd.duration && (
                          <span className="text-xs text-gray-500 whitespace-nowrap">{cmd.duration.toFixed(1)}s</span>
                        )}
                      </div>
                    </summary>

                    <div className="px-2 pb-2 space-y-1.5">
                      {/* AI Fix Display - Enhanced */}
                      {cmd.status === 'ai_fixed' && cmd.aiFix && (
                        <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded p-2 overflow-hidden">
                          <div className="flex items-start gap-2 mb-2">
                            <span className="text-lg">🔧</span>
                            <div className="flex-1">
                              <h5 className="text-xs font-semibold text-blue-300">Automatically Fixed by AI</h5>
                              <p className="text-xs text-gray-400 mt-0.5">
                                The system detected an issue and applied a fix without interrupting your build.
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div>
                              <div className="flex items-center gap-1 mb-1">
                                <span className="text-xs text-red-400 font-medium">❌ Original Problem</span>
                              </div>
                              <code className="text-xs text-red-300 bg-red-900/20 px-2 py-1 rounded block overflow-x-auto whitespace-pre-wrap break-all max-w-full">
                                {cmd.aiFix.original}
                              </code>
                              <p className="text-xs text-gray-400 mt-1 leading-relaxed">{cmd.aiFix.reason}</p>
                            </div>

                            <div>
                              <div className="flex items-center gap-1 mb-1">
                                <span className="text-xs text-green-400 font-medium">✅ Applied Fix</span>
                              </div>
                              <code className="text-xs text-green-300 bg-green-900/20 px-2 py-1 rounded block overflow-x-auto whitespace-pre-wrap break-all max-w-full">
                                {cmd.aiFix.fixed}
                              </code>
                            </div>

                            <div className="bg-gray-900/50 rounded p-2 mt-2">
                              <p className="text-xs text-blue-300 leading-relaxed">
                                <span className="font-medium">Impact:</span> This change was tested and confirmed working.
                                It will be saved to your template for future builds, preventing this issue from recurring.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Stdout - Compact */}
                      {cmd.stdout && (
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Output:</p>
                          <pre className="text-xs text-gray-300 bg-gray-900/50 p-1.5 rounded overflow-x-auto max-h-24">
                            {cmd.stdout}
                          </pre>
                        </div>
                      )}

                      {/* Stderr - User-Friendly Translation */}
                      {cmd.stderr && (() => {
                        const errorInfo = translateError(cmd.stderr, cmd.command);
                        const actionability = getErrorActionability(cmd.stderr);
                        return (
                          <div>
                            <div className="bg-red-900/10 border-l-4 border-red-500 p-2 rounded mb-2">
                              <p className="text-xs text-red-300 font-medium mb-1">
                                {errorInfo.summary}
                              </p>
                              {(errorInfo.actionable || actionability) && (
                                <p className="text-xs text-gray-400 mt-1">
                                  💡 {errorInfo.actionable || actionability}
                                </p>
                              )}
                            </div>
                            <details className="mt-1">
                              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">
                                View technical details
                              </summary>
                              <pre className="text-xs text-red-300 bg-red-900/20 p-1.5 rounded mt-1 overflow-x-auto max-h-24">
                                {cmd.stderr}
                              </pre>
                            </details>
                          </div>
                        );
                      })()}

                      {/* AI Analysis - Compact */}
                      {cmd.aiAnalysis && (
                        <div className="bg-blue-900/20 border border-blue-500/30 rounded p-1.5">
                          <p className="text-xs text-blue-300">{cmd.aiAnalysis}</p>
                        </div>
                      )}

                      {/* Running Progress - Compact */}
                      {cmd.status === 'running' && (
                        <div className="bg-blue-900/20 rounded p-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                            <p className="text-xs text-blue-300">Executing...</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </details>
                ))}
              </div>
            ) : (
              <div className="text-xs text-gray-400 text-center py-2">
                {providerState.status === 'queued' ? 'Waiting...' :
                 providerState.status === 'launching' ? 'Launching VM...' :
                 providerState.status === 'building' ? 'Building image...' :
                 'Initializing...'}
              </div>
            )}

            {/* Result Synthesis - Success */}
            {providerState.status === 'complete' && (
              <div className="bg-green-900/20 border border-green-500/30 rounded p-2 mt-3">
                <div className="flex items-start gap-2">
                  <span className="text-green-400 font-bold text-lg">✓</span>
                  <div className="flex-1">
                    <p className="text-xs text-green-300 font-medium">Validation Successful</p>
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                      All {providerState.commands.filter(c => c.status === 'success' || c.status === 'ai_fixed').length} commands
                      executed successfully in {providerState.duration?.toFixed(0)}s.
                      {providerState.aiFixesApplied > 0 &&
                        ` AI automatically fixed ${providerState.aiFixesApplied} issue${providerState.aiFixesApplied > 1 ? 's' : ''}.`
                      } Your template is ready for the full image build.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Result Synthesis - Failure */}
            {providerState.status === 'failed' && (
              <div className="bg-red-900/20 border border-red-500/30 rounded p-2 mt-3">
                <div className="flex items-start gap-2">
                  <span className="text-red-400 font-bold text-lg">✗</span>
                  <div className="flex-1">
                    <p className="text-xs text-red-300 font-medium">Validation Failed</p>
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                      Command {providerState.commands.findIndex(c => c.status === 'failed') + 1} of {providerState.commands.length} failed.
                      Review the error details above and either fix your template manually or accept AI-suggested fixes if available.
                    </p>
                  </div>
                </div>
              </div>
            )}
            </>)}
          </div>
        ))}
          </div>
        </details>
      </div>

      {/* Infrastructure Health Checks - Compact */}
      {infraHealthChecks.length > 0 && (
        <div className="bg-gray-900/30 border border-gray-700/50 rounded-lg p-3">
          <h3 className="text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">Pre-Flight Checks</h3>
          <div className="space-y-1.5">
            {infraHealthChecks.map((check, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="text-sm">
                  {check.status === 'pass' ? '✅' : check.status === 'warn' ? '⚠️' : '❌'}
                </span>
                <div className="flex-1">
                  <p className="text-xs text-gray-300">{check.check}</p>
                  {check.message && (
                    <p className="text-xs text-gray-400">{check.message}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};
